import {
  AccountListItemButton,
  ErrorListItem,
  ListContentIsland,
  ListItem,
  NoAccountsListItem,
  SkeletonListItem,
  TextField,
  Collapse,
} from '@razz-team/ui-web';
import { ReactNode, useDeferredValue, useMemo, useState } from 'react';
import { ApiAccount } from '../../../openapi';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';

export function AccountList({
  title,
  actions,
  accounts,
  error,
  isLoading,
  isSearchEnabled = false,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  accounts: Array<ApiAccount> | undefined;
  error: Error | null | undefined;
  isLoading: boolean;
  isSearchEnabled?: boolean;
}) {
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const filteredAccounts = useMemo(() => {
    const q = deferredQuery.trim().toLocaleLowerCase();
    if (!accounts || q === '') return accounts ?? [];
    return accounts.filter((account) => {
      if (account.id === q) return true;
      if (account.balance.currency.toLocaleLowerCase().startsWith(q)) return true;
      if (account.name?.toLocaleLowerCase().includes(q)) return true;
      return false;
    });
  }, [accounts, deferredQuery]);

  const hasResults = (filteredAccounts?.length ?? 0) > 0;

  return (
    <ListContentIsland title={title} actions={actions}>
      {isSearchEnabled && (
        <ListItem>
          <TextField
            type="search"
            label={t('Search')}
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            fullWidth
          />
        </ListItem>
      )}
      {isLoading ? (
        <>
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
          <SkeletonListItem variant="withButton" />
        </>
      ) : error ? (
        <ErrorListItem error={error} />
      ) : hasResults ? (
        <TransitionGroup>
          {(isSearchEnabled ? filteredAccounts : (accounts ?? [])).map((account) => (
            <Collapse key={account.id} in timeout={100}>
              <ListItem disablePadding>
                <AccountListItemButton<'a'> account={account} href={`/account/${account.id}`} />
              </ListItem>
            </Collapse>
          ))}
        </TransitionGroup>
      ) : (
        <NoAccountsListItem />
      )}
    </ListContentIsland>
  );
}
